/**
 * VoiceDemoHero.jsx — live voice demo for the hero. One component, zero deps
 * beyond React. Talks to demo_voice_server.py (Railway) over one WebSocket:
 * mic up as raw PCM16 @16k (binary), bot voice down as raw PCM16 @24k
 * (binary), JSON text frames for everything else (captions, sms_image,
 * sms_receipt, error, busy, ending, bye, clear_audio).
 *
 * Images arrive INSIDE the sms_image event as data URLs, generated by
 * demo_voice_server.py — no /public/demo assets required. DEMO_ASSETS is
 * only for optionally overriding a specific id with a real photo.
 * One agent, one scenario: the visitor is "Maria," a known customer; her
 * receipt is pre-seeded on the phone (seedReceipt — sync with server).
 * Mic requires HTTPS (or localhost). Headphones give the cleanest barge-in.
 */

import React, { useEffect, useRef, useState } from 'react';

// ─── tweak these ────────────────────────────────────────────────────────
const DEFAULT_SERVER = 'wss://exitium-production.up.railway.app/ws';
const ACCENT = '#3b82f6';   // site blue
const ACCENT2 = '#8b5cf6';  // gradient partner
const DEMO_ASSETS = {
  // Optional local overrides. Images now arrive FROM THE SERVER inside the
  // sms_image event as data URLs (generated in demo_voice_server.py), so
  // this map stays empty unless you want to swap a specific id for a real
  // photo: e.g. 'sec-899': '/demo/sec-899.jpg' (beats the server image).
};
// ────────────────────────────────────────────────────────────────────────

// One agent, one scenario: the visitor plays "Maria," an existing customer
// the store's caller ID recognizes. Her receipt is pre-seeded on the demo
// phone below so the living-receipt UI is visible before the mic starts.
// KEEP THE PAYLOAD IN SYNC with _receipt_payload() in demo_voice_server.py.
const DEMO_BLURB = 'The agent recognizes your number and has your order open. Ask about your delivery or your balance, or shop for something new. Pictures and receipts show up on the phone.';
const DEMO_BLURB_MOBILE = 'The agent recognizes your number and has your order open. Ask about your delivery or your balance, or shop for something new.';

function seedThread() {
  return [
    {
      kind: 'text',
      key: 'seed-hello',
      text: 'Hi Maria, here\u2019s your itemized receipt. Reply to this thread anytime with questions about your order.',
    },
    seedReceipt(),
  ];
}

function seedReceipt() {
  return {
    kind: 'receipt',
    key: 'seed-receipt',
    payload: {
      store: 'Exitium Demo Showroom',
      customer: 'Maria Lopez',
      order_id: 'e7a41c92',
      items: [
        { qty: 1, name: 'Sectional', color: 'Slate Gray', price: 1299, status: 'On the delivery truck' },
        { qty: 1, name: 'Coffee Table', color: 'Walnut', price: 199, status: 'Ready at the store' },
        { qty: 1, name: 'Dining Set', color: 'Rustic Oak', price: 499, status: 'On order' },
      ],
      total: 1997.0,
      paid: 1000.0,
      balance: 997.0,
      delivery: 'Truck arriving today \u00b7 12 PM - 3 PM',
      services: 'Setup & assembly included',
      footer: 'On a real order this thread is a live chat. Customers text questions and the agent answers from their receipt.',
    },
  };
}

// Inline AudioWorklet: downsample mic (ctx rate, usually 48k) → 16k PCM16
// mono, ~40ms chunks, plus a coarse level ping for the visualizer.
const WORKLET_CODE = `
class MicDownsampler extends AudioWorkletProcessor {
  constructor() {
    super();
    this.target = 16000;
    this.ratio = sampleRate / this.target;
    this.acc = [];
    this.readPos = 0;
  }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (!ch) return true;
    for (let i = 0; i < ch.length; i++) this.acc.push(ch[i]);
    const outLen = Math.floor((this.acc.length - 1 - this.readPos) / this.ratio);
    if (outLen >= 640) {
      const out = new Int16Array(outLen);
      let peak = 0;
      for (let i = 0; i < outLen; i++) {
        const pos = this.readPos + i * this.ratio;
        const i0 = Math.floor(pos);
        const frac = pos - i0;
        const s = this.acc[i0] * (1 - frac) + this.acc[i0 + 1] * frac;
        const a = Math.abs(s); if (a > peak) peak = a;
        out[i] = Math.max(-32768, Math.min(32767, Math.round(s * 32767)));
      }
      this.readPos += outLen * this.ratio;
      const drop = Math.floor(this.readPos);
      this.acc = this.acc.slice(drop);
      this.readPos -= drop;
      this.port.postMessage({ pcm: out.buffer, level: peak }, [out.buffer]);
    }
    return true;
  }
}
registerProcessor('mic-downsampler', MicDownsampler);
`;

// Below lg the demo is rebuilt, not reflowed: the phone goes on TOP and the
// orb sits UNDER it. The old layout let flex-wrap decide, and because the
// agent panel is `flex: 1 1 380px` it always wrapped first - so on a phone you
// got the talk control sitting on top of the phone you were supposed to be
// watching. matchMedia is used rather than a CSS reorder because every style
// in this file is an inline object; CSS `order` alone can't resize the orb or
// swap its label.
function useNarrow(query = '(max-width: 1023px)') {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(query);
    const onChange = (e) => setNarrow(e.matches);
    setNarrow(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, [query]);
  return narrow;
}

export default function VoiceDemoHero({ serverUrl = DEFAULT_SERVER }) {
  const narrow = useNarrow();
  const [phase, setPhase] = useState('idle'); // idle|connecting|live|ending|ended|busy|error
  const [errorMsg, setErrorMsg] = useState('');
  const [captions, setCaptions] = useState([]);
  const [messages, setMessages] = useState(() => seedThread());
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [level, setLevel] = useState(0);
  const [botTalking, setBotTalking] = useState(false);

  const wsRef = useRef(null);
  const ctxRef = useRef(null);
  const micStreamRef = useRef(null);
  const workletNodeRef = useRef(null);
  const playCursorRef = useRef(0);
  const sourcesRef = useRef(new Set());
  const outRateRef = useRef(24000);
  const timerRef = useRef(null);
  const phoneEndRef = useRef(null);

  useEffect(() => () => stopAll(), []); // unmount cleanup
  useEffect(() => {
    // Scroll ONLY the phone panel, never the page.
    const el = phoneEndRef.current;
    if (el && el.parentElement) el.parentElement.scrollTop = el.parentElement.scrollHeight;
  }, [messages]);

  function pushCaption(role, text) {
    setCaptions((c) => [...c.slice(-3), { role, text, key: Date.now() + Math.random() }]);
  }
  function pushBubble(b) {
    setMessages((m) => [...m, { ...b, key: Date.now() + Math.random() }]);
  }

  function playPcm(arrayBuf) {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const i16 = new Int16Array(arrayBuf);
    if (!i16.length) return;
    const f32 = new Float32Array(i16.length);
    for (let i = 0; i < i16.length; i++) f32[i] = i16[i] / 32768;
    const buf = ctx.createBuffer(1, f32.length, outRateRef.current);
    buf.getChannelData(0).set(f32);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    const startAt = Math.max(ctx.currentTime + 0.06, playCursorRef.current);
    src.start(startAt);
    playCursorRef.current = startAt + buf.duration;
    sourcesRef.current.add(src);
    src.onended = () => {
      sourcesRef.current.delete(src);
      if (ctxRef.current && playCursorRef.current <= ctxRef.current.currentTime + 0.05) {
        setBotTalking(false);
      }
    };
    setBotTalking(true);
  }

  function clearAudio() {
    sourcesRef.current.forEach((s) => { try { s.stop(); } catch (e) {} });
    sourcesRef.current.clear();
    if (ctxRef.current) playCursorRef.current = ctxRef.current.currentTime;
    setBotTalking(false);
  }

  async function start() {
    setErrorMsg('');
    setCaptions([]);
    setMessages(seedThread());
    setPhase('connecting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      micStreamRef.current = stream;

      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx;
      await ctx.resume();
      playCursorRef.current = ctx.currentTime;

      const blobUrl = URL.createObjectURL(
        new Blob([WORKLET_CODE], { type: 'application/javascript' })
      );
      await ctx.audioWorklet.addModule(blobUrl);
      URL.revokeObjectURL(blobUrl);

      const srcNode = ctx.createMediaStreamSource(stream);
      const worklet = new AudioWorkletNode(ctx, 'mic-downsampler');
      workletNodeRef.current = worklet;
      srcNode.connect(worklet);

      const ws = new WebSocket(`${serverUrl}?mode=demo`);
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      worklet.port.onmessage = (e) => {
        if (e.data && e.data.pcm && ws.readyState === WebSocket.OPEN) {
          ws.send(e.data.pcm);
        }
        if (e.data && typeof e.data.level === 'number') {
          setLevel((prev) => prev * 0.7 + e.data.level * 0.3);
        }
      };

      ws.onopen = () => setPhase('live');
      ws.onmessage = (ev) => {
        if (ev.data instanceof ArrayBuffer) { playPcm(ev.data); return; }
        let msg; try { msg = JSON.parse(ev.data); } catch (e) { return; }
        if (msg.type === 'hello') {
          outRateRef.current = msg.out_rate || 24000;
          startCountdown(msg.max_secs || 150);
        } else if (msg.type === 'caption') {
          pushCaption(msg.role, msg.text);
        } else if (msg.type === 'sms_image') {
          pushBubble({ kind: 'image', id: msg.id, label: msg.label, price: msg.price, src: msg.src });
        } else if (msg.type === 'sms_receipt') {
          pushBubble({ kind: 'receipt', payload: msg.payload });
        } else if (msg.type === 'clear_audio') {
          clearAudio();
        } else if (msg.type === 'busy') {
          setPhase('busy'); stopMedia();
        } else if (msg.type === 'ending') {
          setPhase('ending');
        } else if (msg.type === 'error') {
          console.error('DEMO SERVER ERROR:\n' + (msg.detail || ''));
          const lines = (msg.detail || '').split('\n').filter(Boolean);
          setErrorMsg('Server: ' + (lines[lines.length - 1] || 'unknown error'));
          setPhase('error');
        } else if (msg.type === 'bye') {
          // server speaks the closing then ends; keep audio flowing
        }
      };
      ws.onerror = () => {
        if (phaseRefSafe() === 'connecting') {
          setErrorMsg('Could not reach the demo server.');
          setPhase('error'); stopMedia();
        }
      };
      ws.onclose = () => {
        stopMedia();
        setPhase((p) => (p === 'busy' || p === 'error' ? p : 'ended'));
      };
    } catch (err) {
      setErrorMsg(
        err && err.name === 'NotAllowedError'
          ? 'Microphone access was blocked. Allow the mic and try again.'
          : 'Could not start the demo: ' + (err && err.message ? err.message : err)
      );
      setPhase('error');
      stopMedia();
    }
  }

  const phaseRef = useRef(phase);
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  function phaseRefSafe() { return phaseRef.current; }

  function startCountdown(total) {
    setSecondsLeft(total);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => (s === null || s <= 1 ? 0 : s - 1));
    }, 1000);
  }

  function stopMedia() {
    clearInterval(timerRef.current);
    clearAudio();
    if (workletNodeRef.current) { try { workletNodeRef.current.disconnect(); } catch (e) {} }
    if (micStreamRef.current) micStreamRef.current.getTracks().forEach((t) => t.stop());
    if (ctxRef.current) { try { ctxRef.current.close(); } catch (e) {} }
    workletNodeRef.current = null; micStreamRef.current = null; ctxRef.current = null;
    setLevel(0); setBotTalking(false); setSecondsLeft(null);
  }

  function stopAll() {
    if (wsRef.current) { try { wsRef.current.close(); } catch (e) {} wsRef.current = null; }
    stopMedia();
  }

  function endDemo() { stopAll(); setPhase('ended'); }

  const live = phase === 'live' || phase === 'ending';
  const connecting = phase === 'connecting';
  const userAmp = Math.min(1, level * 3.2);

  // ── styles ───────────────────────────────────────────────────────────
  const S = {
    wrap: { display: 'flex', flexWrap: 'wrap', gap: 36, alignItems: 'stretch',
      justifyContent: 'center', padding: '8px 4px', color: '#0f172a',
      fontFamily: 'inherit', textAlign: 'left' },
    panel: { flex: '1 1 380px', maxWidth: 500,
      background: '#ffffff',
      border: '1px solid #e2e8f0', borderRadius: 26,
      boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 24px 60px -24px rgba(15,23,42,0.18)',
      padding: '26px 26px 22px', display: 'flex', flexDirection: 'column',
      alignItems: 'center', gap: 18 },
    eyebrow: { display: 'flex', alignItems: 'center', gap: 8,
      padding: '7px 14px', borderRadius: 999,
      background: '#eff6ff',
      border: '1px solid #bfdbfe', color: '#1d4ed8',
      fontSize: 11, fontWeight: 700, letterSpacing: 1.4,
      textTransform: 'uppercase' },
    eyebrowDot: { width: 7, height: 7, borderRadius: '50%',
      background: '#4ade80', boxShadow: '0 0 8px rgba(74,222,128,0.8)',
      animation: 'exdemo-pulse 1.6s ease-in-out infinite' },
    blurb: { fontSize: 13, color: '#64748b', textAlign: 'center',
      minHeight: 20, margin: 0, lineHeight: 1.5 },
    talkWrap: { position: 'relative', width: 160, height: 160, margin: '2px 0',
      display: 'flex', alignItems: 'center', justifyContent: 'center' },
    ringSvg: { position: 'absolute', inset: 0, overflow: 'visible' },
    disc: { position: 'relative', width: 126, height: 126, borderRadius: '50%',
      cursor: 'pointer', border: '1px solid rgba(255,255,255,0.14)',
      background: 'radial-gradient(120% 120% at 30% 22%, rgba(59,130,246,0.24), rgba(13,15,24,0.92) 58%)',
      boxShadow: live
        ? `0 0 ${botTalking ? 52 : 30}px rgba(59,130,246,0.35), inset 0 1px 0 rgba(255,255,255,0.12)`
        : 'inset 0 1px 0 rgba(255,255,255,0.1), 0 12px 30px rgba(37,99,235,0.22)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'box-shadow 240ms, transform 180ms',
      outline: 'none' },
    eqRow: { display: 'flex', alignItems: 'center', gap: 5, height: 48 },
    eqBar: (i) => {
      const mult = [0.55, 0.95, 1, 0.8, 0.6][i];
      const h = botTalking ? undefined : Math.max(9, 9 + userAmp * 40 * mult);
      return {
        width: 4.5, borderRadius: 3,
        height: h !== undefined ? h : 10,
        background: `linear-gradient(180deg, ${ACCENT}, ${ACCENT2})`,
        boxShadow: `0 0 10px ${ACCENT}66`,
        transition: botTalking ? 'none' : 'height 90ms linear',
        animation: botTalking ? `exdemo-eq 0.72s ease-in-out ${i * 0.09}s infinite` : 'none',
      };
    },
    micIcon: { opacity: connecting ? undefined : 0.92,
      animation: connecting ? 'exdemo-pulse 1.1s ease-in-out infinite' : 'none' },
    status: { fontSize: 12.5, color: '#64748b', minHeight: 18,
      textAlign: 'center', lineHeight: 1.5 },
    captions: { width: '100%', minHeight: 100, flex: '1 1 auto', overflow: 'hidden',
      display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'flex-end' },
    capRole: (role) => ({ fontSize: 9, fontWeight: 700, letterSpacing: 1.6,
      textTransform: 'uppercase', marginBottom: 2,
      color: role === 'bot' ? '#94a3b8' : '#2563eb',
      textAlign: role === 'bot' ? 'left' : 'right' }),
    capText: (role) => ({ fontSize: 13.5, lineHeight: 1.5,
      color: role === 'bot' ? '#0f172a' : '#2563eb',
      textAlign: role === 'bot' ? 'left' : 'right' }),
    endBtn: { padding: '8px 20px', borderRadius: 999, fontSize: 12.5,
      border: '1px solid #cbd5e1', background: '#ffffff',
      color: '#334155', cursor: 'pointer' },
    fine: { fontSize: 10, color: '#94a3b8', textAlign: 'center' },

    // ── phone ──
    // The wrap has alignItems:'stretch', so on one row this column is given the
    // agent panel's exact height; bezel→screen→thread flex-fill down the chain
    // so the phone body is always the same height as the panel next to it.
    phoneCol: { flex: '0 1 340px', width: 'min(340px, 100%)', minHeight: 0,
      position: 'relative', display: 'flex', flexDirection: 'column', gap: 10 },
    // Out of the column's flow (floats just below the phone) so the bezel gets
    // 100% of the stretched height and its top/bottom line up with the panel.
    phoneCaption: { position: 'absolute', top: '100%', left: '50%',
      transform: 'translateX(-50%)', marginTop: 10, whiteSpace: 'nowrap',
      textAlign: 'center', fontSize: 11,
      color: '#94a3b8', letterSpacing: 0.3 },
    bezel: { position: 'relative', borderRadius: 46, padding: 10, flex: 1,
      minHeight: 0, display: 'flex', flexDirection: 'column',
      background: 'linear-gradient(160deg, #3c4250, #14161e 45%, #0b0c12)',
      boxShadow: '0 40px 90px rgba(0,0,0,0.55), inset 0 1px 1px rgba(255,255,255,0.18)' },
    sideBtn: (top, h) => ({ position: 'absolute', left: -2, top, width: 3,
      height: h, borderRadius: 2, background: 'rgba(255,255,255,0.22)' }),
    powerBtn: { position: 'absolute', right: -2, top: 150, width: 3, height: 64,
      borderRadius: 2, background: 'rgba(255,255,255,0.22)' },
    screen: { position: 'relative', borderRadius: 38, overflow: 'hidden', flex: 1,
      minHeight: 0,
      background: 'linear-gradient(180deg, #0a0c14 0%, #07080d 100%)',
      border: '1px solid rgba(255,255,255,0.05)', display: 'flex',
      flexDirection: 'column' },
    chatHeader: { display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 3, padding: '4px 10px 8px',
      borderBottom: '1px solid rgba(255,255,255,0.07)' },
    avatar: { width: 30, height: 30, borderRadius: '50%',
      background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT2})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 13, fontWeight: 700, color: '#fff' },
    chatName: { fontSize: 10.5, color: 'rgba(255,255,255,0.75)',
      letterSpacing: 0.2 },
    textMsg: { padding: '10px 13px', fontSize: 13, lineHeight: 1.45,
      color: 'rgba(255,255,255,0.92)' },
    statusBar: { display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', padding: '13px 24px 4px', height: 34 },
    sbTime: { fontSize: 12.5, fontWeight: 600, color: '#e5e7eb',
      fontVariantNumeric: 'tabular-nums' },
    island: { position: 'absolute', top: 11, left: '50%',
      transform: 'translateX(-50%)', width: 96, height: 24, borderRadius: 13,
      background: '#000', border: '1px solid rgba(255,255,255,0.04)' },
    sbIcons: { display: 'flex', alignItems: 'center', gap: 5 },
    sigBar: (h) => ({ width: 2.5, height: h, borderRadius: 1,
      background: '#e5e7eb', alignSelf: 'flex-end' }),
    battery: { width: 20, height: 10, borderRadius: 3,
      border: '1px solid rgba(229,231,235,0.6)', padding: 1.5,
      marginLeft: 2, display: 'flex' },
    batteryFill: { flex: '0 0 72%', borderRadius: 1.5, background: '#e5e7eb' },
    thread: { flex: 1, minHeight: 0, overflowY: 'auto',
      display: 'flex', flexDirection: 'column', gap: 10,
      padding: '10px 14px 18px' },
    dayStamp: { textAlign: 'center', fontSize: 10.5,
      color: 'rgba(255,255,255,0.32)', margin: '4px 0 2px' },
    bubble: { alignSelf: 'flex-start', maxWidth: '88%',
      background: '#24252d', borderRadius: '18px 18px 18px 6px',
      border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden',
      animation: 'exdemo-msgIn 0.35s cubic-bezier(0.2,0.8,0.3,1) both' },
    bubbleImg: { width: '100%', display: 'block', aspectRatio: '4/3',
      objectFit: 'cover' },
    imgPlaceholder: { width: '100%', aspectRatio: '4/3', display: 'flex',
      alignItems: 'center', justifyContent: 'center', textAlign: 'center',
      padding: 14, fontSize: 13.5, fontWeight: 700, color: '#eaf1ff',
      letterSpacing: 0.2, lineHeight: 1.4,
      background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT2})` },
    bubbleMeta: { padding: '9px 13px', fontSize: 12.5,
      color: 'rgba(255,255,255,0.88)' },
    rc: { padding: '13px 15px 12px', fontSize: 12.5, color: '#1c2536' },
    rcStore: { fontWeight: 700, fontSize: 13, textAlign: 'center',
      color: '#111a2e' },
    rcOrder: { color: '#67718a', textAlign: 'center',
      fontSize: 11, marginTop: 1 },
    rcRow: { display: 'flex', justifyContent: 'space-between', gap: 10,
      padding: '3px 0' },
    rcHr: { border: 'none', borderTop: '1px dashed rgba(28,37,54,0.22)',
      margin: '9px 0' },
    rcStatus: (status) => {
      const s = (status || '').toLowerCase();
      const c = s.includes('ready')
        ? ['#15803d', 'rgba(22,163,74,0.13)']
        : (s.includes('truck') || s.includes('deliver') || s.includes('transit'))
          ? ['#2563eb', 'rgba(37,99,235,0.11)']
          : ['#c2620a', 'rgba(236,140,22,0.16)'];
      return { display: 'inline-block', marginLeft: 6, padding: '2px 8px',
        borderRadius: 999, fontSize: 10, fontWeight: 700,
        letterSpacing: 0.2, whiteSpace: 'nowrap',
        color: c[0], background: c[1] };
    },
  };

  // ── narrow (<1024px) overrides ──────────────────────────────────────────
  // No phone on mobile. The simulated handset never had room to be legible
  // next to a control you have to reach, so below lg the demo is the agent
  // panel alone: orb, status, transcript. That frees enough height for a
  // bigger orb and a transcript you can actually read while talking.
  S.mWrap = { display: 'flex', justifyContent: 'center', width: '100%',
    color: '#0f172a', fontFamily: 'inherit', textAlign: 'left' };
  S.mPanel = { ...S.panel, flex: '1 1 auto', width: '100%', maxWidth: 440,
    padding: '22px 18px 18px', gap: 14 };
  S.mBlurb = { ...S.blurb, fontSize: 12.5, minHeight: 0 };
  S.mTalkWrap = { position: 'relative', width: 152, height: 152, margin: '2px 0',
    display: 'flex', alignItems: 'center', justifyContent: 'center' };
  // The label lives INSIDE the orb - it replaces the separate "tap to talk"
  // prompt row, which is the line there was never room for.
  S.mDisc = { ...S.disc, width: 128, height: 128, flexDirection: 'column',
    gap: 5, padding: 6, textAlign: 'center' };
  S.mDiscLabel = { fontSize: 11, fontWeight: 700, letterSpacing: 0.8,
    textTransform: 'uppercase', color: '#1d4ed8', lineHeight: 1 };
  S.mCaps = { ...S.captions, minHeight: 108, maxHeight: 240 };
  S.mStatus = { ...S.status, fontSize: 12, minHeight: 17 };

  const statusText =
    phase === 'idle' ? 'Tap to talk. Allow the mic, then just speak.'
    : connecting ? 'Connecting…'
    : phase === 'live' ? (secondsLeft !== null ? `Live · ${secondsLeft}s · headphones sound best` : 'Live')
    : phase === 'ending' ? 'Wrapping up…'
    : phase === 'busy' ? 'All demo lines are busy. Try again in a minute.'
    : phase === 'error' ? errorMsg
    : phase === 'ended' ? 'Demo ended. Tap to go again.'
    : '';

  const orbLabel =
    live ? 'Tap to end'
    : connecting ? 'Connecting'
    : phase === 'ended' ? 'Go again'
    : (phase === 'busy' || phase === 'error') ? 'Try again'
    : 'Tap to talk';

  const lastCaptions = captions.slice(-2);

  // Short status for narrow screens - the long "tap to talk" prompt is now
  // carried by the orb label itself, so this row only speaks up when it has
  // something the label doesn't already say.
  const shortStatus =
    connecting ? 'Connecting…'
    : phase === 'live' ? (secondsLeft !== null ? `Live · ${secondsLeft}s · headphones sound best` : 'Live')
    : phase === 'ending' ? 'Wrapping up…'
    : phase === 'busy' ? 'All demo lines are busy. Try again shortly.'
    : phase === 'error' ? errorMsg
    : phase === 'ended' ? 'Demo ended.'
    : 'Allow the mic, then just speak.';

  const keyframes = (
    <style>{`
      @keyframes exdemo-pulse { 0%,100% { opacity: .45 } 50% { opacity: 1 } }
      @keyframes exdemo-spin { to { transform: rotate(360deg) } }
      @keyframes exdemo-eq { 0%,100% { height: 10px } 50% { height: 42px } }
      @keyframes exdemo-msgIn {
        from { opacity: 0; transform: translateY(10px) scale(.97) }
        to { opacity: 1; transform: translateY(0) scale(1) }
      }
      @keyframes exdemo-capIn {
        from { opacity: 0; transform: translateY(6px) }
        to { opacity: 1; transform: translateY(0) }
      }
      /* Side-by-side (desktop): phone proportions + a height cap — the
         thread scrolls inside the phone (like a real one) instead of the
         receipt stretching the whole section. Panel still mirrors the
         phone's height exactly via align-items: stretch. */
      @media (min-width: 1024px) {
        .exdemo-phonecol { flex: 0 0 380px !important; width: 380px !important;
          max-height: 660px; }
        .exdemo-caps { max-height: 210px; }
      }
    `}</style>
  );

  // ── the orb ── `compact` puts the label inside the disc and shrinks it to
  // fit under the phone on a handset.
  const renderOrb = (compact = false) => (
    <div style={compact ? S.mTalkWrap : S.talkWrap}>
      <svg style={S.ringSvg} viewBox="0 0 160 160" aria-hidden="true">
        <defs>
          <linearGradient id="exdemo-ring" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={ACCENT} />
            <stop offset="100%" stopColor={ACCENT2} />
          </linearGradient>
        </defs>
        <circle cx="80" cy="80" r="72" fill="none"
          stroke="rgba(255,255,255,0.10)" strokeWidth="1" />
        {(live || connecting) && (
          <g style={{ transformOrigin: '80px 80px',
                      animation: `exdemo-spin ${connecting ? 1.1 : 3.4}s linear infinite` }}>
            <circle cx="80" cy="80" r="72" fill="none"
              stroke="url(#exdemo-ring)" strokeWidth="2"
              strokeLinecap="round" strokeDasharray="70 383" />
          </g>
        )}
      </svg>

      <div role="button" tabIndex={0} style={compact ? S.mDisc : S.disc}
           onClick={() => (live ? endDemo() : connecting ? null : start())}
           onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); live ? endDemo() : start(); } }}
           onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.04)'; }}
           onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
           aria-label={live ? 'End demo' : 'Start voice demo'}>
        {live ? (
          <div style={compact ? { ...S.eqRow, height: 34 } : S.eqRow}>
            {[0, 1, 2, 3, 4].map((i) => <div key={i} style={S.eqBar(i)} />)}
          </div>
        ) : (
          <svg width={compact ? 26 : 34} height={compact ? 26 : 34} viewBox="0 0 24 24" fill="none"
               style={S.micIcon} aria-hidden="true">
            <rect x="9" y="2.5" width="6" height="11.5" rx="3"
              stroke="#dbeafe" strokeWidth="1.6" />
            <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0" stroke="#dbeafe"
              strokeWidth="1.6" strokeLinecap="round" />
            <path d="M12 18v3.5M8.5 21.5h7" stroke="#dbeafe"
              strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        )}
        {compact && <div style={S.mDiscLabel}>{orbLabel}</div>}
      </div>
    </div>
  );

  // ── the simulated customer phone ── shared by both layouts
  const phoneBody = (
    <div style={S.bezel}>
      <div style={S.sideBtn(120, 30)} aria-hidden="true" />
      <div style={S.sideBtn(160, 52)} aria-hidden="true" />
      <div style={S.powerBtn} aria-hidden="true" />
      <div style={S.screen}>
        <div style={S.island} aria-hidden="true" />
        <div style={S.statusBar}>
          <span style={S.sbTime}>9:41</span>
          <span style={S.sbIcons} aria-hidden="true">
            <span style={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
              <span style={S.sigBar(4)} /><span style={S.sigBar(6)} />
              <span style={S.sigBar(8)} /><span style={S.sigBar(10)} />
            </span>
            <svg width="15" height="11" viewBox="0 0 15 11" style={{ marginLeft: 3 }}>
              <path d="M7.5 10.5 1 4.2a9.2 9.2 0 0 1 13 0Z" fill="#e5e7eb" />
            </svg>
            <span style={S.battery}><span style={S.batteryFill} /></span>
          </span>
        </div>
        <div style={S.chatHeader}>
          <div style={S.avatar} aria-hidden="true">E</div>
          <div style={S.chatName}>Exitium Demo Showroom</div>
        </div>
        <div className="exdemo-thread" style={S.thread}>
          <div style={S.dayStamp}>Today 9:41 AM</div>
          {messages.map((m) => m.kind === 'image' ? (
            <div key={m.key} style={S.bubble}>
              {(DEMO_ASSETS[m.id] || m.src)
                ? <img src={DEMO_ASSETS[m.id] || m.src} alt={m.label} style={S.bubbleImg} />
                : <div style={S.imgPlaceholder}>{m.label}</div>}
              <div style={S.bubbleMeta}>{m.label} · {m.price}</div>
            </div>
          ) : m.kind === 'text' ? (
            <div key={m.key} style={S.bubble}>
              <div style={S.textMsg}>{m.text}</div>
            </div>
          ) : (
            <div key={m.key} style={{ ...S.bubble, maxWidth: '94%', background: '#f5f7fb', border: '1px solid rgba(255,255,255,0.4)' }}>
              <div style={S.rc}>
                <div style={S.rcStore}>{m.payload.store}</div>
                <div style={S.rcOrder}>{m.payload.customer ? `${m.payload.customer} \u00b7 ` : ''}Order #{m.payload.order_id}</div>
                <hr style={S.rcHr} />
                {m.payload.items.map((it, i) => (
                  <div key={i} style={S.rcRow}>
                    <span>{it.qty}x {it.name}<br />
                      <span style={{ fontSize: 11, color: '#67718a' }}>{it.color}</span>
                      <span style={S.rcStatus(it.status)}>{it.status}</span>
                    </span>
                    <span>${it.price.toLocaleString()}</span>
                  </div>
                ))}
                <hr style={S.rcHr} />
                <div style={S.rcRow}><span>Total</span><span>${m.payload.total.toFixed(2)}</span></div>
                <div style={S.rcRow}><span>Paid</span><span>${m.payload.paid.toFixed(2)}</span></div>
                <div style={{ ...S.rcRow, color: '#2563eb', fontWeight: 700 }}>
                  <span>Balance</span><span>${m.payload.balance.toFixed(2)}</span>
                </div>
                <hr style={S.rcHr} />
                <div>{m.payload.delivery} · {m.payload.services}</div>
                <div style={{ marginTop: 8, fontSize: 10.5, color: '#67718a' }}>
                  {m.payload.footer}
                </div>
              </div>
            </div>
          ))}
          <div ref={phoneEndRef} />
        </div>
      </div>
    </div>
  );

  // ══ NARROW: the agent alone - no simulated phone ══
  if (narrow) {
    return (
      <div style={S.mWrap}>
        {keyframes}

        <div style={S.mPanel}>
          <div style={S.eyebrow}>
            <span style={S.eyebrowDot} aria-hidden="true" />
            Live call · you&apos;re &ldquo;Maria&rdquo;
          </div>
          <p style={S.mBlurb}>{DEMO_BLURB_MOBILE}</p>

          {renderOrb(true)}

          <div style={S.mStatus}>{shortStatus}</div>

          <div style={S.mCaps}>
            {captions.map((c) => (
              <div key={c.key} style={{ animation: 'exdemo-capIn 0.25s ease both' }}>
                <div style={S.capRole(c.role)}>{c.role === 'bot' ? 'Agent' : 'You'}</div>
                <div style={S.capText(c.role)}>{c.text}</div>
              </div>
            ))}
          </div>

          {live && <button style={S.endBtn} onClick={endDemo}>End demo</button>}
          <div style={S.fine}>Live AI demo. Audio is processed to run the conversation.</div>
        </div>
      </div>
    );
  }

  // ══ WIDE: original two-panel layout ══
  return (
    <div style={S.wrap}>
      {keyframes}

      {/* ── left: the agent ── */}
      <div style={S.panel}>
        <div style={S.eyebrow}>
          <span style={S.eyebrowDot} aria-hidden="true" />
          Live call · you&apos;re &ldquo;Maria&rdquo;
        </div>
        <p style={S.blurb}>{DEMO_BLURB}</p>

        {renderOrb(false)}

        <div style={S.status}>{statusText}</div>

        <div className="exdemo-caps" style={S.captions}>
          {captions.map((c) => (
            <div key={c.key} style={{ animation: 'exdemo-capIn 0.25s ease both' }}>
              <div style={S.capRole(c.role)}>{c.role === 'bot' ? 'Agent' : 'You'}</div>
              <div style={S.capText(c.role)}>{c.text}</div>
            </div>
          ))}
        </div>

        {live && <button style={S.endBtn} onClick={endDemo}>End demo</button>}
        <div style={S.fine}>Live AI demo. Audio is processed to run the conversation.</div>
      </div>

      {/* ── right: the simulated customer phone ── */}
      <div className="exdemo-phonecol" style={S.phoneCol}>
        <div style={S.phoneCaption}>Your customer&apos;s phone, simulated. No real texts are sent.</div>
        {phoneBody}
      </div>
    </div>
  );
}